// ONE upload handshake for everything the Docs tab puts in S3 — a requirement
// source's original, the rasterised screens of an approved build preview.
//
// Two transports, because the browser's NETWORK POSITION is not ours to assume:
//
//   * DIRECT — presigned PUT, the handshake the requirement intake, board
//     attachments and chat uploads use. The signer returns a BROWSER-facing
//     S3 host (localhost:9000 in local dev). Fast, streams straight to S3, but
//     only works from a browser that can reach that host: a Cloudflare tunnel,
//     an iPad, a colleague's laptop cannot, and an http PUT from an https page
//     is refused as mixed content before it is tried. On 2026-09-04 every
//     approved-preview screen picture failed this way from a remote browser —
//     silently, instantly — while every Playwright run on the Mac passed.
//   * PROXY — the bytes go to the backend (`POST /api/docs/uploads`), which
//     puts them into S3 itself. The only host involved is the one that served
//     the page, so it works from anywhere the app does.
//
// Small files (the screen captures are ~0.5 MB each) take the proxy FIRST — a
// failing direct PUT costs a round trip and a scary console error before the
// fallback runs. Large originals try the direct PUT first and fall back to the
// proxy on ANY failure of the PUT (thrown fetch, mixed content, a non-2xx).
const PROXY_FIRST_BYTES = 25 * 1024 * 1024;

function authHeaders(projectSlug) {
  const token = localStorage.getItem("access_token") || "";
  return {
    Authorization: `Bearer ${token}`,
    "X-Cortex-Project": projectSlug,
    "X-Cortex-Branch": "main",
  };
}

async function readError(res) {
  let msg = `HTTP ${res.status}`;
  try { const j = await res.json(); msg = j.message || j.error || msg; } catch { /* keep the status */ }
  return msg;
}

async function uploadViaProxy(file, projectSlug) {
  const q = new URLSearchParams({ filename: file.name, content_type: file.type || "application/octet-stream" });
  let res;
  try {
    res = await fetch(`/api/docs/uploads?${q}`, {
      method: "POST",
      headers: { ...authHeaders(projectSlug), "Content-Type": "application/octet-stream" },
      body: file,
    });
  } catch {
    throw new Error("the app could not reach the server — check that it is running, then try again");
  }
  if (!res.ok) throw new Error(await readError(res));
  const { key } = await res.json();
  if (!key) throw new Error("the server stored the file but returned no key");
  return key;
}

async function uploadDirect(file, projectSlug) {
  let signed;
  try {
    signed = await fetch("/api/chat/upload/sign", {
      method: "POST",
      headers: { ...authHeaders(projectSlug), "Content-Type": "application/json" },
      body: JSON.stringify({ filename: file.name, content_type: file.type || "application/octet-stream" }),
    });
  } catch {
    throw Object.assign(new Error("the app could not reach the server — check that it is running, then try again"), { server: true });
  }
  if (!signed.ok) throw Object.assign(new Error(await readError(signed)), { server: true });
  const { url, key } = await signed.json();
  // From here on a failure is the S3 HOST, not the server — that is the case
  // the proxy exists for.
  let put;
  try {
    put = await fetch(url, { method: "PUT", headers: { "Content-Type": file.type || "application/octet-stream" }, body: file });
  } catch (e) {
    throw new Error(`direct upload failed (${String(e?.message || e).slice(0, 80)})`);
  }
  if (!put.ok) throw new Error(`direct upload failed (HTTP ${put.status})`);
  return key;
}

export async function uploadOriginal(file, projectSlug) {
  if (file.size <= PROXY_FIRST_BYTES) {
    try { return await uploadViaProxy(file, projectSlug); }
    catch (e) {
      // 4xx from the proxy is a decision (auth, project, size) the direct path
      // would repeat; a server that is down is the same for both. Only a
      // storage failure behind the proxy is worth the second attempt.
      if (!/could not be stored|Bad Gateway|HTTP 5\d\d/i.test(String(e?.message || ""))) throw e;
      return uploadDirect(file, projectSlug);
    }
  }
  try { return await uploadDirect(file, projectSlug); }
  catch (e) {
    if (e?.server) throw e;
    return uploadViaProxy(file, projectSlug);
  }
}
